import { useAuth } from '@/stores/auth';
import {
  DepartmentAccessLevelDTO,
  TravellerWithCollectionMembershipsDTO,
} from '@/api/trafikportalen';
import { ExtendedUserDTO } from '@/hooks/useGetAllAdmins';

/**
 * Check if the current user has any access to a specific department
 * This function works without hooks and can be used in beforeLoad functions
 */
export const hasDepartmentAccess = (departmentId: string): boolean => {
  const { departmentAccess } = useAuth.getState();

  if (!departmentAccess?.departments) {
    return false;
  }

  const access = departmentAccess.departments[departmentId];
  return !!access && access.access_level !== DepartmentAccessLevelDTO.none;
};

/**
 * Check if the current user has write access to a specific department
 * This function works without hooks and can be used in beforeLoad functions
 */
export const hasDepartmentWriteAccess = (departmentId: string): boolean => {
  const { departmentAccess } = useAuth.getState();

  if (!departmentAccess?.departments) {
    return false;
  }

  const access = departmentAccess.departments[departmentId];
  return access?.access_level === DepartmentAccessLevelDTO.editor;
};

const grantsBearerManagement = (level?: DepartmentAccessLevelDTO) =>
  level === DepartmentAccessLevelDTO['bearer-manager'] ||
  level === DepartmentAccessLevelDTO.editor;

/**
 * Check if the current user may change bearers and cards in a department:
 * bearer managers and editors may, viewers may not.
 */
export const hasDepartmentBearerManagerAccess = (
  departmentId: string,
): boolean => {
  const { departmentAccess } = useAuth.getState();
  return grantsBearerManagement(
    departmentAccess?.departments?.[departmentId]?.access_level,
  );
};

/**
 * Get the access level for a specific department
 * Returns 'none' if no access is found
 */
export const getDepartmentAccessLevel = (
  departmentId: string,
): DepartmentAccessLevelDTO => {
  const { departmentAccess } = useAuth.getState();

  if (!departmentAccess?.departments) {
    return DepartmentAccessLevelDTO.none;
  }

  const access = departmentAccess.departments[departmentId];
  return access?.access_level || DepartmentAccessLevelDTO.none;
};

/**
 * Check if the current user has access to any departments
 * Useful for checking if a user is a department user vs organization admin
 */
export const hasAnyDepartmentAccess = (): boolean => {
  const { departmentAccess } = useAuth.getState();
  return (departmentAccess?.accessible_departments || 0) > 0;
};

export const hasAnyDepartmentWriteAccess = (): boolean => {
  const { departmentAccess } = useAuth.getState();
  return (departmentAccess?.editor_departments || 0) > 0;
};

/**
 * Check if the current user is a department user (has department access but no organization permissions)
 * This is the key check mentioned in the task - department users have empty permissions by design
 */
export const isDepartmentUser = (): boolean => {
  const { permissions } = useAuth.getState();
  const hasOrgPermissions = permissions && permissions.length > 0;
  const hasDeptAccess = hasAnyDepartmentAccess();

  // Department user: no organization permissions but has department access
  return !hasOrgPermissions && hasDeptAccess;
};

/**
 * Check if the current user can access a specific view/action in a department
 * This combines permission checking for organization admins and department access for department users
 */
export const canAccessDepartmentResource = (
  departmentId: string,
  requireWriteAccess: boolean = false,
): boolean => {
  const { permissions } = useAuth.getState();

  if (permissions && permissions.length > 0) {
    const requiredPermission = requireWriteAccess
      ? 'organization:editor'
      : 'organization:viewer';
    return (
      permissions.includes(requiredPermission) ||
      permissions.includes('organization:admin')
    );
  }

  if (requireWriteAccess) {
    return hasDepartmentWriteAccess(departmentId);
  } else {
    return hasDepartmentAccess(departmentId);
  }
};

/**
 * Get all department IDs that the user has access to
 */
export const getAccessibleDepartmentIds = (): string[] => {
  const { departmentAccess } = useAuth.getState();

  if (!departmentAccess?.departments) {
    return [];
  }

  return Object.keys(departmentAccess.departments).filter(
    (departmentId) =>
      departmentAccess.departments[departmentId]?.access_level !==
      DepartmentAccessLevelDTO.none,
  );
};

/**
 * Get all department IDs that the user has write access to
 */
export const getWriteAccessDepartmentIds = (): string[] => {
  const { departmentAccess } = useAuth.getState();

  if (!departmentAccess?.departments) {
    return [];
  }

  return Object.keys(departmentAccess.departments).filter(
    (departmentId) =>
      departmentAccess.departments[departmentId]?.access_level ===
      DepartmentAccessLevelDTO.editor,
  );
};

/**
 * Check if the current user has department access for any of the traveller's memberships
 */
export const hasAnyMembershipDepartmentAccess = (
  traveller: TravellerWithCollectionMembershipsDTO,
): boolean => {
  const { departmentAccess } = useAuth.getState();

  if (!departmentAccess?.departments || !traveller.memberships) {
    return false;
  }

  return traveller.memberships.some((membership) =>
    hasDepartmentAccess(membership.department_id),
  );
};

/**
 * Check if the current user has department write access for any of the traveller's memberships
 */
export const hasAnyMembershipDepartmentWriteAccess = (
  traveller: TravellerWithCollectionMembershipsDTO,
): boolean => {
  const { departmentAccess } = useAuth.getState();

  if (!departmentAccess?.departments || !traveller.memberships) {
    return false;
  }

  return traveller.memberships.some((membership) => {
    const userDepartmentAccess =
      departmentAccess.departments[membership.department_id];
    return (
      userDepartmentAccess?.access_level === DepartmentAccessLevelDTO.editor
    );
  });
};

/**
 * Check if the current user may change bearers and cards on the traveller
 * through any of the traveller's memberships
 */
export const hasAnyMembershipDepartmentBearerManagerAccess = (
  traveller: TravellerWithCollectionMembershipsDTO,
): boolean =>
  traveller.memberships?.some((membership) =>
    hasDepartmentBearerManagerAccess(membership.department_id),
  ) ?? false;

export const hasWriteAccessToAllDepartments = (
  administrator: ExtendedUserDTO,
): boolean => {
  return (
    administrator.departments?.every((department) =>
      hasDepartmentWriteAccess(department.department_id),
    ) || false
  );
};
